<script lang="ts" setup>
import { ref, computed } from 'vue'
import type { Component } from 'vue'
import Button from './Button.vue'
import Message from './Message.vue'

import { t } from '@/lib/locale'

const props = defineProps<{
  quantity: number
  frequency: number
  isWorking: boolean
  floatingMesage?: {
    text: string
    Icon: Component
  }
}>()

const emit = defineEmits<{
  (e: 'saveChanges', changes: { frequency: number; quantity: number }): void
}>()

const hasChanges = computed(
  () =>
    props.quantity !== newQuantity.value ||
    props.frequency !== newFrequency.value
)
const newQuantity = ref(props.quantity)
const newFrequency = ref(props.frequency)

const onQuantityChange = (event: Event) => {
  newQuantity.value = parseInt((event.target as HTMLSelectElement).value)
}

const onFrequencyChange = (event: Event) => {
  newFrequency.value = parseInt((event.target as HTMLSelectElement).value)
}

const hasFloatingMessage = props.floatingMesage
  ? 'SubscriptionManager--has-floating-message'
  : ''

const save = () =>
  emit('saveChanges', {
    frequency: newFrequency.value,
    quantity: newQuantity.value,
  })
</script>

<template>
  <div class="SubscriptionManager Card" :class="hasFloatingMessage">
    <h3 class="SubscriptionManager__Title h4">
      {{ t('purchase_item_subscription_manager_title') }}
    </h3>
    <p class="SubscriptionManager__Updates">
      {{ t('purchase_item_subscription_manager_prefix') }}
      <span class="SubscriptionManager__LineTwo">
        <select @change="onQuantityChange" :disabled="isWorking">
          <option
            v-for="n in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
            :value="n"
            :selected="quantity === n"
            :key="n"
          >
            {{ n }}
          </option>
        </select>
        {{ t('purchase_item_subscription_manager_midfix') }}
        <select @change="onFrequencyChange" :disabled="isWorking">
          <option
            v-for="n in [1, 2, 3, 4, 5, 6]"
            :value="n"
            :selected="frequency === n"
            :key="n"
          >
            {{ n }} month{{ n < 2 ? '' : 's' }}
          </option>
        </select>
      </span>
    </p>
    <div>
      <Button
        :disabled="!hasChanges || isWorking"
        :modifiers="['inverted']"
        @click="save"
        >{{ t('purchase_item_subscription_manager_save_cta') }}</Button
      >
    </div>
    <div class="SubscriptionManager__FloatingMessage" v-if="floatingMesage">
      <Message mode="mid" pointerPosition="topleft" pointerDirection="vertical">
        <template #icon>
          <component :is="floatingMesage.Icon" class="Icon" />
        </template>
        {{ floatingMesage.text }}
      </Message>
    </div>
  </div>
</template>

<style>
.SubscriptionManager {
  padding: 24px;
  position: relative;
}

.SubscriptionManager--has-floating-message {
  padding-bottom: 35px;
}

.SubscriptionManager__Title {
  margin: 0;
}

.SubscriptionManager__Updates {
  margin: 8px 0 16px;
}

.SubscriptionManager__LineTwo {
  display: block;
  margin-top: 8px;
  @media (--mq-tablet-and-up) {
    display: inline;
    margin-top: 0;
  }
}

.SubscriptionManager .Message--error {
  margin-top: 16px;
}

.SubscriptionManager__FloatingMessage {
  position: absolute;
  bottom: 20px;
  left: 25px;
  transform: translateY(100%);
  max-width: 465px;
}
</style>
